<script setup>
    import { computed } from 'vue';
    import { useMainStore } from '@/stores/store.js';
    import { PhWarningCircle, PhAsterisk } from '@phosphor-icons/vue';
    const store = useMainStore();
</script>
<!--
START STEP
-->
<template>
    <div
        id="step-start"
        class="lux-step-root">
        <h2 class="lux-page-title">Welcome to the Luxxbox Acoustic Analyzer</h2>
        <p class="lux-copy-normal">
            This user-friendly tool will help you quickly and easily generate a customized acoustic report for your project. By providing some basic
            information about your space and selecting your preferred Luxxbox acoustic lighting products, you can get a clear understanding of the
            recommended reverberation time and the number of products needed to achieve optimal sound quality.
        </p>
        <div class="lux-mt flex w-full items-stretch justify-start gap-0 bg-white">
            <div
                class="lux-px min-h-[400px] w-1/5 bg-gray-100 bg-[url('@/assets/lux-feature-image-1.webp')] bg-cover bg-[-30px_20%] bg-no-repeat transition-all sm:w-2/5 sm:bg-[0_20%]"></div>
            <div class="relative w-full bg-white px-4 py-6 text-black sm:px-12 sm:py-10">
                <p class="mb-8 text-lg font-medium uppercase text-black">Enter your contact information to get started</p>
                <div class="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 first-of-type:mt-0 sm:grid-cols-6">
                    <!--
                        FIRST NAME
                    -->
                    <div class="sm:col-span-3 sm:col-start-1">
                        <label
                            for="firstname"
                            class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                            First name
                            <span class="sr-only">(required)</span>
                            <PhAsterisk
                                weight="bold"
                                class="lux-required-mark"
                                aria-hidden="true" />
                        </label>
                        <div class="mt-2">
                            <input
                                type="text"
                                name="firstname"
                                id="firstname"
                                v-model="store.user.firstname"
                                placeholder="First name"
                                aria-labelledby="firstname"
                                class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-xs sm:text-sm sm:leading-6" />
                        </div>
                        <p
                            class="mt-1 text-sm text-red-600"
                            id="roomwidth-error"
                            v-if="!store.validateFirstName && store.validation.Start.showErrors">
                            {{ store.validation.Start.errors.firstname }}
                        </p>
                    </div>
                    <!--
                        LAST NAME
                    -->
                    <div class="sm:col-span-3">
                        <label
                            for="lastname"
                            class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                            Last name
                            <span class="sr-only">(required)</span>
                            <PhAsterisk
                                weight="bold"
                                class="lux-required-mark"
                                aria-hidden="true" />
                        </label>
                        <div class="mt-2">
                            <input
                                type="text"
                                name="lastname"
                                id="lastname"
                                v-model="store.user.lastname"
                                placeholder="Last name"
                                aria-labelledby="lastname"
                                class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-xs sm:text-sm sm:leading-6" />
                        </div>
                        <p
                            class="mt-1 text-sm text-red-600"
                            id="first-error"
                            v-if="!store.validateLastName && store.validation.Start.showErrors">
                            {{ store.validation.Start.errors.lastname }}
                        </p>
                    </div>
                    <!--
                        COMPANY
                    -->
                    <div class="sm:col-span-3">
                        <label
                            for="company"
                            class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                            Company
                            <span class="sr-only">(required)</span>
                            <PhAsterisk
                                weight="bold"
                                class="lux-required-mark"
                                aria-hidden="true" />
                        </label>
                        <div class="mt-2">
                            <input
                                type="text"
                                name="company"
                                id="company"
                                v-model="store.user.company"
                                placeholder="Company name"
                                aria-labelledby="company"
                                class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-xs sm:text-sm sm:leading-6" />
                        </div>
                        <p
                            class="mt-1 text-sm text-red-600"
                            id="first-error"
                            v-if="!store.validateCompany && store.validation.Start.showErrors">
                            {{ store.validation.Start.errors.company }}
                        </p>
                    </div>
                    <!--
                        EMAIL
                    -->
                    <div class="sm:col-span-3">
                        <label
                            for="email"
                            class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                            Email Address
                            <span class="sr-only">(required)</span>
                            <PhAsterisk
                                weight="bold"
                                class="lux-required-mark"
                                aria-hidden="true" />
                        </label>
                        <div class="mt-2">
                            <input
                                type="text"
                                name="email"
                                id="email"
                                v-model="store.user.email"
                                placeholder="Email"
                                aria-labelledby="email"
                                class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-xs sm:text-sm sm:leading-6" />
                        </div>
                        <p
                            class="mt-1 text-sm text-red-600"
                            id="first-error"
                            v-if="!store.validateEmail && store.validation.Start.showErrors">
                            {{ store.validation.Start.errors.email }}
                        </p>
                    </div>
                </div>
                <div class="mt-10 text-center">
                    <button
                        type="button"
                        class="lux-btn lux-btn-small lux-btn-bare font-normal"
                        @click="store.handleClearData(store.user.currentStepName)">
                        Clear Form
                    </button>
                </div>
            </div>
        </div>
        <span class="hidden">
            <input
                type="hidden"
                name="stepStartValid"
                :value="store.stepStartIsValid"
                class="hidden" />
        </span>
    </div>
</template>
